import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';
import { CapacityReservationsBoard, MaterialCatalogBoard } from '@api/BoardSDK';
import { readRateCardConfig } from '@generated/server/rate-card.server';
import { resolveWeightColumn } from '@generated/server/material-columns.server';

// ---------------------------------------------------------------------------
// Launch gate: on initial load, prove every piece of configuration this app depends on is
// present, unambiguous, and usable BEFORE any calculation or submission is allowed to happen.
// Fail CLOSED — a single missing/duplicated/invalid field blocks the whole form. Every reason is
// logged server-side for operations; the client only ever sees the generic unavailable message.
// ---------------------------------------------------------------------------

export interface LaunchConfigResult {
  ok: boolean;
  reasons: string[];
}

export const validateLaunchConfig = createServerFn({ method: 'GET' })
  .validator(z.object({}).optional())
  .handler(async (): Promise<LaunchConfigResult> => {
    const reasons: string[] = [];

    // --- Material Catalog: every active material must be usable ---------------------------
    try {
      const catalog = new MaterialCatalogBoard();
      const res = await catalog
        .items()
        .withColumns(['fillLbs', 'requiresUpload', 'sort', 'mapsTo', 'active'])
        .where({ active: true })
        .withPagination({ limit: 100 })
        .execute();

      if (res.items.length === 0) {
        reasons.push('Material Catalog has no active materials.');
      }

      const seenMapsTo = new Map<string, string>();
      for (const it of res.items) {
        if (!it.mapsTo || it.mapsTo.trim() === '') {
          reasons.push(`Material Catalog item "${it.name}" is missing "Maps To".`);
          continue;
        }
        const resolvedColumn = resolveWeightColumn(it.mapsTo);
        if (!resolvedColumn) {
          reasons.push(`Material Catalog item "${it.name}" has "Maps To"="${it.mapsTo}" which is not a known Capacity Reservations column.`);
          continue;
        }
        const priorOwner = seenMapsTo.get(resolvedColumn);
        if (priorOwner) {
          reasons.push(`Duplicate "Maps To" (resolves to the same column) on both "${priorOwner}" and "${it.name}".`);
        } else {
          seenMapsTo.set(resolvedColumn, it.name);
        }
        if (it.sort == null) reasons.push(`Material Catalog item "${it.name}" is missing "Sort".`);
        if (it.requiresUpload == null) reasons.push(`Material Catalog item "${it.name}" is missing "Requires Upload".`);
      }
    } catch (e) {
      console.error('Launch config check: Material Catalog unreadable', e);
      reasons.push('Material Catalog board could not be read.');
    }

    // --- Rate Card: every format + the Global settings row must carry usable numbers -------
    try {
      const { formats, global, globalRowCount, globalRowNames } = await readRateCardConfig();

      if (globalRowCount === 0) {
        reasons.push('Rate Card has no row named "Global Settings" (or close variant) to hold the Box Max Monthly / Volumetric Fill Min / Volumetric Fill Max settings.');
      } else if (globalRowCount > 1) {
        reasons.push(
          `Rate Card has ${globalRowCount} rows named like "Global Settings" — there must be exactly one. Rename or remove the extra: ${globalRowNames.join(', ')}.`,
        );
      }

      if (formats.length === 0) {
        reasons.push('Rate Card has no format rows.');
      }
      for (const f of formats) {
        if (f.nominalFillLbs == null) reasons.push(`Rate Card "${f.name}" is missing "Nominal Fill lbs".`);
        if (f.handlingPerUnit == null && f.pricePerTon == null) {
          reasons.push(`Rate Card "${f.name}" has neither "Handling per Unit" nor "Price per Ton" set.`);
        }
        if (f.ratesEffective == null) reasons.push(`Rate Card "${f.name}" is missing "Rates Effective".`);
      }
      // Duplicate format names would make format selection/lookup by id ambiguous downstream.
      const nameCounts = new Map<string, number>();
      for (const f of formats) nameCounts.set(f.name, (nameCounts.get(f.name) ?? 0) + 1);
      for (const [name, count] of nameCounts) {
        if (count > 1) reasons.push(`Rate Card has ${count} rows named "${name}" — format names must be unique.`);
      }

      if (global.boxMaxMonthlyLbs == null) reasons.push('Rate Card Global row is missing "Box Max Monthly lbs".');
      if (global.volumetricFillMinLbs == null) reasons.push('Rate Card Global row is missing "Volumetric Fill Min lbs".');
      if (global.volumetricFillMaxLbs == null) reasons.push('Rate Card Global row is missing "Volumetric Fill Max lbs".');
    } catch (e) {
      console.error('Launch config check: Rate Card unreadable', e);
      reasons.push('Rate Card board could not be read.');
    }

    // --- Capacity Reservations: board must be reachable and writable-shaped ----------------
    try {
      const board = new CapacityReservationsBoard();
      await board.items().withColumns(['status', 'submissionKey']).withPagination({ limit: 1 }).execute();
    } catch (e) {
      console.error('Launch config check: Capacity Reservations board unreachable', e);
      reasons.push('Capacity Reservations board could not be read.');
    }

    if (reasons.length > 0) {
      console.error('Launch config gate failed:', reasons);
    }

    return { ok: reasons.length === 0, reasons };
  });
